if (!customElements.get('multi-quantity-form')) {
  customElements.define(
    'multi-quantity-form',
    class MultiQuantityForm extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.section;
        this.sizeOptionPosition = parseInt(this.dataset.sizeOptionPosition);
        this.colorOptionPosition = this.dataset.colorOptionPosition
          ? parseInt(this.dataset.colorOptionPosition)
          : null;

        this.gridWrapper = this.querySelector('.multi-quantity__grid-wrapper');
        this.rows = this.querySelectorAll('.multi-quantity__grid-row');
        this.qtyInputs = this.querySelectorAll('.multi-quantity__qty-input');
        this.submitBtn = this.querySelector('.multi-quantity__submit');
        this.totalCount = this.querySelector('.multi-quantity__total-count');
        this.totalPrice = this.querySelector('.multi-quantity__total-price');
        this.toggleBtns = this.querySelectorAll('.multi-quantity__toggle-btn');

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.bindEvents();

        if (this.colorOptionPosition) {
          this.filterRowsByColor(this.getSelectedColor());
        }
      }

      bindEvents() {
        // Mode toggle
        this.toggleBtns.forEach((btn) => {
          btn.addEventListener('click', () => this.setMode(btn.dataset.mode));
        });

        // Quantity inputs
        this.qtyInputs.forEach((input) => {
          input.addEventListener('input', () => this.updateTotals());
          input.addEventListener('change', () => {
            const val = parseInt(input.value);
            if (isNaN(val) || val < 0) input.value = 0;
            this.updateTotals();
          });
        });

        // Submit
        this.submitBtn.addEventListener('click', () => this.onSubmit());

        // Listen for color changes from variant-selects
        if (this.colorOptionPosition) {
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data?.sectionId !== this.sectionId) return;
            const colorOptionKey = `option${this.colorOptionPosition}`;
            const newColor = event.data?.variant?.[colorOptionKey];
            if (newColor) {
              this.filterRowsByColor(newColor);
            }
          });

          // Also listen for direct radio/select changes as a fallback
          const variantSelects = document.querySelector(`#variant-selects-${this.sectionId}`);
          if (variantSelects) {
            variantSelects.addEventListener('change', () => {
              const color = this.getSelectedColor();
              if (color) this.filterRowsByColor(color);
            });
          }
        }
      }

      getSelectedColor() {
        if (!this.colorOptionPosition) return null;

        // Try radio buttons first (button picker)
        const variantSelects = document.querySelector(`#variant-selects-${this.sectionId}`);
        if (!variantSelects) return null;

        const fieldsets = variantSelects.querySelectorAll('fieldset');
        const colorFieldset = fieldsets[this.colorOptionPosition - 1];
        if (!colorFieldset) return null;

        const checkedRadio = colorFieldset.querySelector('input[type="radio"]:checked');
        if (checkedRadio) return checkedRadio.value;

        // Try dropdown
        const select = colorFieldset.querySelector('select');
        if (select) return select.value;

        return null;
      }

      filterRowsByColor(color) {
        if (!color) return;

        this.rows.forEach((row) => {
          const rowColor = row.dataset.variantColor;
          if (rowColor === color) {
            row.style.display = '';
          } else {
            row.style.display = 'none';
            // Reset hidden row quantities
            const input = row.querySelector('.multi-quantity__qty-input');
            if (input) input.value = 0;
          }
        });

        this.updateTotals();
      }

      setMode(mode) {
        this.toggleBtns.forEach((btn) => {
          btn.classList.toggle('multi-quantity__toggle-btn--active', btn.dataset.mode === mode);
        });

        const isMulti = mode === 'multi';
        this.gridWrapper.style.display = isMulti ? '' : 'none';

        // Hide/show the normal variant picker, quantity selector, and buy buttons
        const singleItemElements = [
          document.querySelector(`#variant-selects-${this.sectionId}`)?.closest('.product-form__input'),
          document.getElementById(`Quantity-Form-${this.sectionId}`),
          document.querySelector(`[data-section-id="${this.sectionId}"] product-form`)?.closest('[data-shopify]'),
        ];

        // In multi mode: hide size picker and qty selector, but keep color picker visible
        if (isMulti && this.colorOptionPosition) {
          const variantSelects = document.querySelector(`#variant-selects-${this.sectionId}`);
          if (variantSelects) {
            const fieldsets = variantSelects.querySelectorAll('fieldset');
            fieldsets.forEach((fieldset, index) => {
              // Hide all option fieldsets except color
              if (index === this.colorOptionPosition - 1) {
                fieldset.style.display = '';
              } else {
                fieldset.style.display = 'none';
              }
            });
          }
        } else {
          // Restore all fieldsets
          const variantSelects = document.querySelector(`#variant-selects-${this.sectionId}`);
          if (variantSelects) {
            variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
              fieldset.style.display = '';
            });
          }
        }

        // Hide/show quantity selector and buy button in multi mode
        const qtyForm = document.getElementById(`Quantity-Form-${this.sectionId}`);
        if (qtyForm) qtyForm.style.display = isMulti ? 'none' : '';

        const productForm = this.closest('.product__info-wrapper')?.querySelector('product-form');
        if (productForm) {
          const buyButtonWrapper = productForm.closest('[data-shopify]') || productForm.parentElement;
          if (buyButtonWrapper) buyButtonWrapper.style.display = isMulti ? 'none' : '';
        }

        // Reset quantities when switching to single mode
        if (!isMulti) {
          this.qtyInputs.forEach((input) => (input.value = 0));
          this.updateTotals();
        }
      }

      updateTotals() {
        let totalQty = 0;
        let totalPriceCents = 0;

        this.rows.forEach((row) => {
          if (row.style.display === 'none') return;
          const input = row.querySelector('.multi-quantity__qty-input');
          if (!input) return;
          const qty = parseInt(input.value) || 0;
          if (qty > 0) {
            totalQty += qty;
            totalPriceCents += qty * parseInt(row.dataset.variantPrice);
          }
        });

        this.totalCount.textContent = totalQty;
        this.totalPrice.textContent = this.formatMoney(totalPriceCents);
        this.submitBtn.disabled = totalQty === 0;
      }

      formatMoney(cents) {
        return '$' + (cents / 100).toFixed(2);
      }

      getLineItemProperties() {
        const properties = {};
        const existingForm = document.querySelector(`#product-form-${this.sectionId}`);
        if (!existingForm) return properties;

        const formData = new FormData(existingForm);
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[') && value) {
            properties[key.replace('properties[', '').replace(']', '')] = value;
          }
        }

        return properties;
      }

      async onSubmit() {
        const items = [];
        const properties = this.getLineItemProperties();

        this.rows.forEach((row) => {
          if (row.style.display === 'none') return;
          const input = row.querySelector('.multi-quantity__qty-input');
          if (!input) return;
          const qty = parseInt(input.value) || 0;
          if (qty > 0) {
            const item = {
              id: parseInt(row.dataset.variantId),
              quantity: qty,
            };
            if (Object.keys(properties).length > 0) {
              item.properties = properties;
            }
            items.push(item);
          }
        });

        if (items.length === 0) return;

        // Show loading state
        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.classList.add('loading');
        const spinner = this.submitBtn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        try {
          const sections = this.cart ? this.cart.getSectionsToRender().map((s) => s.id || s.section) : [];

          const body = JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname,
          });

          const response = await fetch(routes.cart_add_url, {
            ...fetchConfig('javascript'),
            body,
          });

          const parsedState = await response.json();

          if (parsedState.status) {
            // Error from Shopify
            console.error('Multi-quantity add error:', parsedState);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'multi-quantity-form',
              errors: parsedState.errors || parsedState.description,
              message: parsedState.message,
            });
            return;
          }

          // Success - update cart UI
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'multi-quantity-form',
            cartData: parsedState,
          });

          if (this.cart) {
            // For multi-item adds, parsedState has { items: [...], sections: {...} }
            // Cart notification expects .key, cart drawer expects .id
            // Use the first added item for notification context
            if (parsedState.items && parsedState.items.length > 0) {
              parsedState.key = parsedState.items[0].key;
              parsedState.id = parsedState.items[0].id;
            }
            this.cart.renderContents(parsedState);
          }

          // Reset quantities after successful add
          this.qtyInputs.forEach((input) => (input.value = 0));
          this.updateTotals();
        } catch (error) {
          console.error('Multi-quantity add failed:', error);
        } finally {
          this.submitBtn.classList.remove('loading');
          this.submitBtn.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
          if (this.cart && this.cart.classList.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }
        }
      }
    }
  );
}
